import React, { useState, useEffect, useCallback } from 'react'
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TextInput,
  TouchableOpacity,
  Image,
  Platform,
  Alert } from 'react-native'
import { ArrowLeft, Check, Package, CheckCircle2, Circle, MapPin, Folder, ChevronDown, ChevronUp } from 'lucide-react-native'
import { supabase } from '../lib/supabase'
import { useLanguage } from '@fliisi/i18n'
import { useAuth } from '../contexts/AuthContext'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { RouteProp } from '@react-navigation/native'
import { RootStackParamList } from '../types/navigation'
import theme from '../theme'
import {
  Button,
  ButtonText,
  Card,
  EmptyState,
  Input,
  Spinner,
  TopBar,
  XStack,
  YStack } from '@fliisi/pollen'
interface Props {
  navigation: NativeStackNavigationProp<RootStackParamList, 'SubmitOffer'>
  route: RouteProp<RootStackParamList, 'SubmitOffer'>
}

interface Store {
  id: string
  name: string
  description: string | null
  address_street: string | null
  address_city: string | null
  offer_dropoff_info: string | null
}

interface Collection {
  id: string
  name: string
  name_fi: string | null
  name_en: string | null
  color: string | null
}

interface DraftProduct {
  id: string
  title: string
  title_en: string | null
  title_fi: string | null
  description: string | null
  price: number | null
  status: string
  collection_id: string | null
  product_images: { image_url: string }[]
}

export default function SubmitOfferScreen({ navigation, route }: Props) {
  const { storeId } = route.params
  const { t, language } = useLanguage()
  const { user } = useAuth()

  const [store, setStore] = useState<Store | null>(null)
  const [draftProducts, setDraftProducts] = useState<DraftProduct[]>([])
  const [collections, setCollections] = useState<Collection[]>([])
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set())
  const [productPrices, setProductPrices] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [notes, setNotes] = useState('')
  const [expandedCollections, setExpandedCollections] = useState<Set<string>>(new Set(['uncategorized']))

  useEffect(() => {
    fetchData()
  }, [storeId])

  const fetchData = async () => {
    try {
      // Fetch store info
      const { data: storeData, error: storeError } = await supabase
        .from('flea_markets')
        .select('id, name, description, address_street, address_city, offer_dropoff_info')
        .eq('id', storeId)
        .single()

      if (storeError) throw storeError
      setStore(storeData)

      // Fetch user's collections
      if (user?.id) {
        const { data: collectionsData } = await supabase
          .from('product_collections')
          .select('id, name, name_fi, name_en, color')
          .eq('user_id', user.id)
          .order('sort_order', { ascending: true })

        if (collectionsData) {
          setCollections(collectionsData)
          // Auto-expand all collections that have products
          const collectionsWithProducts = new Set(['uncategorized'])
          collectionsData.forEach(c => collectionsWithProducts.add(c.id))
          setExpandedCollections(collectionsWithProducts)
        }
      }

      // Fetch user's draft products (not linked to any reservation)
      if (user?.id) {
        const { data: productsData, error: productsError } = await supabase
          .from('products')
          .select(`
            id,
            title,
            title_en,
            title_fi,
            description,
            price,
            status,
            collection_id,
            product_images (image_url)
          `)
          .eq('seller_id', user.id)
          .is('reservation_id', null)
          .is('flea_market_id', null)
          .in('status', ['draft', 'for_sale'])
          .order('created_at', { ascending: false })

        if (!productsError && productsData) {
          setDraftProducts(productsData)
          // Initialize prices from products
          const initialPrices: Record<string, string> = {}
          productsData.forEach(p => {
            if (p.price) initialPrices[p.id] = p.price.toString()
          })
          setProductPrices(initialPrices)
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error)
      showAlert(t('error') || 'Error', t('store_not_found') || 'Store not found')
      navigation.goBack()
    } finally {
      setLoading(false)
    }
  }

  const showAlert = (title: string, message: string) => {
    if (Platform.OS === 'web') {
      window.alert(message)
    } else {
      Alert.alert(title, message)
    }
  }

  const getTitle = (product: DraftProduct) => {
    if (language === 'fi' && product.title_fi) return product.title_fi
    if (language === 'en' && product.title_en) return product.title_en
    return product.title || t('untitled')
  }

  const toggleProduct = (productId: string) => {
    const newSelected = new Set(selectedProducts)
    if (newSelected.has(productId)) {
      newSelected.delete(productId)
    } else {
      newSelected.add(productId)
    }
    setSelectedProducts(newSelected)
  }

  const updatePrice = (productId: string, price: string) => {
    setProductPrices({ ...productPrices, [productId]: price })
  }

  const getCollectionName = (collection: Collection) => {
    if (language === 'fi' && collection.name_fi) return collection.name_fi
    if (language === 'en' && collection.name_en) return collection.name_en
    return collection.name
  }

  const toggleCollectionExpand = (collectionId: string) => {
    const newExpanded = new Set(expandedCollections)
    if (newExpanded.has(collectionId)) {
      newExpanded.delete(collectionId)
    } else {
      newExpanded.add(collectionId)
    }
    setExpandedCollections(newExpanded)
  }

  const selectEntireCollection = (collectionId: string | null) => {
    const collectionProducts = draftProducts.filter(p =>
      collectionId === null ? !p.collection_id : p.collection_id === collectionId
    )
    const allSelected = collectionProducts.every(p => selectedProducts.has(p.id))

    const newSelected = new Set(selectedProducts)
    if (allSelected) {
      // Deselect all in this collection
      collectionProducts.forEach(p => newSelected.delete(p.id))
    } else {
      // Select all in this collection
      collectionProducts.forEach(p => newSelected.add(p.id))
    }
    setSelectedProducts(newSelected)
  }

  const getProductsByCollection = useCallback(() => {
    const uncategorized = draftProducts.filter(p => !p.collection_id)
    const byCollection: Record<string, DraftProduct[]> = {}

    collections.forEach(c => {
      byCollection[c.id] = draftProducts.filter(p => p.collection_id === c.id)
    })

    return { uncategorized, byCollection }
  }, [draftProducts, collections])

  const { uncategorized, byCollection } = getProductsByCollection()

  const calculateTotal = (): number => {
    let total = 0
    selectedProducts.forEach(id => {
      const price = parseFloat(productPrices[id]) || 0
      total += price
    })
    return total
  }

  const validateForm = (): boolean => {
    if (selectedProducts.size === 0) {
      showAlert(t('error') || 'Error', t('select_at_least_one_product') || 'Please select at least one product')
      return false
    }

    for (const id of selectedProducts) {
      const price = productPrices[id]
      if (!price || isNaN(parseFloat(price)) || parseFloat(price) <= 0) {
        showAlert(t('error') || 'Error', t('enter_valid_price_all') || 'Please enter a valid price for all selected products')
        return false
      }
    }
    return true
  }

  const submitOffer = async () => {
    if (!user?.id) {
      showAlert(t('error') || 'Error', t('login_required') || 'Please log in to submit an offer')
      return
    }

    if (!validateForm()) return

    setSubmitting(true)
    try {
      // Create the offer
      const { data: offer, error: offerError } = await supabase
        .from('seller_offers')
        .insert({
          seller_id: user.id,
          store_id: storeId,
          total_price: calculateTotal(),
          description: notes.trim() || null,
          seller_notes: notes.trim() || null })
        .select()
        .single()

      if (offerError) throw offerError

      // Create offer items from selected products
      const selectedProductsList = draftProducts.filter(p => selectedProducts.has(p.id))

      if (selectedProductsList.length === 0) {
        await supabase.from('seller_offers').delete().eq('id', offer.id)
        throw new Error(t('no_products_selected') || 'No products selected')
      }

      for (const product of selectedProductsList) {
        const price = parseFloat(productPrices[product.id]) || 0
        const imageUrl = product.product_images?.[0]?.image_url || null

        const { data: offerItem, error: itemError } = await supabase
          .from('offer_items')
          .insert({
            offer_id: offer.id,
            product_id: product.id,
            title: getTitle(product),
            description: product.description || null,
            price: price })
          .select()
          .single()

        if (itemError) {
          // Roll back the offer header to avoid orphaned offers
          await supabase.from('seller_offers').delete().eq('id', offer.id)
          throw itemError
        }

        // Link existing product images to the offer
        if (product.product_images && product.product_images.length > 0) {
          for (let i = 0; i < product.product_images.length; i++) {
            await supabase
              .from('offer_images')
              .insert({
                offer_item_id: offerItem.id,
                image_url: product.product_images[i].image_url,
                sort_order: i })
          }
        }
      }

      showAlert(
        t('success') || 'Success',
        t('offer_submitted') || 'Your offer has been submitted! The store will review it and get back to you.'
      )
      navigation.goBack()
    } catch (error: any) {
      console.error('Error submitting offer:', error)
      showAlert(t('error') || 'Error', error.message || t('offer_submit_failed') || 'Failed to submit offer')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
          <Text style={styles.headerTitle}>{t('submit_offer') || 'Submit Offer'}</Text>
          <View style={styles.headerSpacer} />
        </View>
        <YStack f={1} jc="center" ai="center"><Spinner size="large" /></YStack>
</View>
    )
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Button variant="ghost" size="sm" onPress={() => navigation.goBack()}><ArrowLeft size={20} color="#748C70" /></Button>
        <View style={styles.headerTitleContainer}>
          <Text style={styles.headerTitle}>{t('submit_offer') || 'Tee tarjous'}</Text>
          {store && <Text style={styles.headerSubtitle}>{store.name}</Text>}
        </View>
        <View style={styles.headerSpacer} />
      </View>

      <ScrollView style={styles.scrollContent} keyboardShouldPersistTaps="handled">
        {/* Store Info */}
        {store && (
          <View style={styles.storeInfo}>
            <Text style={styles.storeInfoLabel}>{t('offer_to') || 'Tarjous kauppaan'}</Text>
            <Text style={styles.storeName}>{store.name}</Text>
            {store.address_city && (
              <View style={styles.storeLocationRow}>
                <MapPin size={14} color={theme.colors.text.secondary} />
                <Text style={styles.storeAddress}>{store.address_city}</Text>
              </View>
            )}
            {store.offer_dropoff_info && (
              <View style={styles.dropoffInfo}>
                <Text style={styles.dropoffLabel}>{t('dropoff_info') || 'Tuontiohjeet'}:</Text>
                <Text style={styles.dropoffText}>{store.offer_dropoff_info}</Text>
              </View>
            )}
          </View>
        )}

        {/* Product Selection */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{t('select_products') || 'Valitse tuotteet'}</Text>
          <Text style={styles.sectionSubtitle}>
            {t('select_products_hint') || 'Valitse tuotteet joita haluat tarjota kauppaan'}
          </Text>

          {draftProducts.length === 0 ? (
            <View style={styles.emptyState}>
              <Package size={48} color={theme.colors.text.secondary} />
              <Text style={styles.emptyStateTitle}>{t('no_products_to_offer') || 'Ei tuotteita'}</Text>
              <Text style={styles.emptyStateText}>
                {t('add_products_first_offer') || 'Lisää ensin tuotteita kameralla tai galleriasta, sitten voit tarjota niitä kaupoille.'}
              </Text>
              <TouchableOpacity
                style={styles.addProductsButton}
                onPress={() => navigation.navigate('Home')}
              >
                <Text style={styles.addProductsButtonText}>{t('add_products') || 'Lisää tuotteita'}</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.productsList}>
              {/* Uncategorized Products */}
              {uncategorized.length > 0 && (
                <View style={styles.collectionGroup}>
                  <TouchableOpacity
                    style={styles.collectionHeader}
                    onPress={() => toggleCollectionExpand('uncategorized')}
                  >
                    <View style={styles.collectionHeaderLeft}>
                      <View style={[styles.colorDot, { backgroundColor: theme.colors.ui.border }]} />
                      <Text style={styles.collectionName}>{t('uncategorized') || 'Uncategorized'}</Text>
                      <Text style={styles.collectionCount}>({uncategorized.length})</Text>
                    </View>
                    <View style={styles.collectionHeaderRight}>
                      <TouchableOpacity
                        style={styles.selectAllButton}
                        onPress={() => selectEntireCollection(null)}
                      >
                        <Text style={styles.selectAllText}>
                          {uncategorized.every(p => selectedProducts.has(p.id))
                            ? (t('deselect_all_items') || 'Deselect')
                            : (t('select_all_items') || 'Select All')}
                        </Text>
                      </TouchableOpacity>
                      {expandedCollections.has('uncategorized') ? (
                        <ChevronUp size={20} color={theme.colors.text.secondary} />
                      ) : (
                        <ChevronDown size={20} color={theme.colors.text.secondary} />
                      )}
                    </View>
                  </TouchableOpacity>

                  {expandedCollections.has('uncategorized') && uncategorized.map((product) => {
                    const isSelected = selectedProducts.has(product.id)
                    const imageUrl = product.product_images?.[0]?.image_url

                    return (
                      <TouchableOpacity
                        key={product.id}
                        style={[styles.productCard, isSelected && styles.productCardSelected]}
                        onPress={() => toggleProduct(product.id)}
                        activeOpacity={0.7}
                      >
                        <View style={styles.productCardContent}>
                          <View style={styles.selectionIndicator}>
                            {isSelected ? (
                              <CheckCircle2 size={24} color={theme.colors.brand.primary} />
                            ) : (
                              <Circle size={24} color={theme.colors.ui.border} />
                            )}
                          </View>
                          {imageUrl ? (
                            <Image source={{ uri: imageUrl }} style={styles.productImage} />
                          ) : (
                            <View style={[styles.productImage, styles.productImagePlaceholder]}>
                              <Package size={24} color={theme.colors.text.secondary} />
                            </View>
                          )}
                          <View style={styles.productInfo}>
                            <Text style={styles.productTitle} numberOfLines={2}>{getTitle(product)}</Text>
                            {product.description && (
                              <Text style={styles.productDescription} numberOfLines={1}>{product.description}</Text>
                            )}
                          </View>
                        </View>
                        {isSelected && (
                          <View style={styles.priceInputRow}>
                            <Text style={styles.priceLabel}>{t('your_price') || 'Hintapyyntösi'}:</Text>
                            <View style={styles.priceInputContainer}>
                              <TextInput
                                style={styles.priceInput}
                                value={productPrices[product.id] || ''}
                                onChangeText={(text) => updatePrice(product.id, text)}
                                placeholder="0.00"
                                placeholderTextColor={theme.colors.text.secondary}
                                keyboardType="decimal-pad"
                              />
                              <Text style={styles.currencyLabel}>€</Text>
                            </View>
                          </View>
                        )}
                      </TouchableOpacity>
                    )
                  })}
                </View>
              )}

              {/* Collection Groups */}
              {collections.map(collection => {
                const collectionProducts = byCollection[collection.id] || []
                if (collectionProducts.length === 0) return null

                const isExpanded = expandedCollections.has(collection.id)
                const allSelected = collectionProducts.every(p => selectedProducts.has(p.id))

                return (
                  <View key={collection.id} style={styles.collectionGroup}>
                    <TouchableOpacity
                      style={styles.collectionHeader}
                      onPress={() => toggleCollectionExpand(collection.id)}
                    >
                      <View style={styles.collectionHeaderLeft}>
                        <View style={[styles.colorDot, { backgroundColor: collection.color || theme.colors.ui.border }]} />
                        <Text style={styles.collectionName}>{getCollectionName(collection)}</Text>
                        <Text style={styles.collectionCount}>({collectionProducts.length})</Text>
                      </View>
                      <View style={styles.collectionHeaderRight}>
                        <TouchableOpacity
                          style={styles.selectAllButton}
                          onPress={() => selectEntireCollection(collection.id)}
                        >
                          <Text style={styles.selectAllText}>
                            {allSelected ? (t('deselect_all_items') || 'Deselect') : (t('select_all_items') || 'Select All')}
                          </Text>
                        </TouchableOpacity>
                        {isExpanded ? (
                          <ChevronUp size={20} color={theme.colors.text.secondary} />
                        ) : (
                          <ChevronDown size={20} color={theme.colors.text.secondary} />
                        )}
                      </View>
                    </TouchableOpacity>

                    {isExpanded && collectionProducts.map((product) => {
                      const isSelected = selectedProducts.has(product.id)
                      const imageUrl = product.product_images?.[0]?.image_url

                      return (
                        <TouchableOpacity
                          key={product.id}
                          style={[styles.productCard, isSelected && styles.productCardSelected]}
                          onPress={() => toggleProduct(product.id)}
                          activeOpacity={0.7}
                        >
                          <View style={styles.productCardContent}>
                            <View style={styles.selectionIndicator}>
                              {isSelected ? (
                                <CheckCircle2 size={24} color={theme.colors.brand.primary} />
                              ) : (
                                <Circle size={24} color={theme.colors.ui.border} />
                              )}
                            </View>
                            {imageUrl ? (
                              <Image source={{ uri: imageUrl }} style={styles.productImage} />
                            ) : (
                              <View style={[styles.productImage, styles.productImagePlaceholder]}>
                                <Package size={24} color={theme.colors.text.secondary} />
                              </View>
                            )}
                            <View style={styles.productInfo}>
                              <Text style={styles.productTitle} numberOfLines={2}>{getTitle(product)}</Text>
                              {product.description && (
                                <Text style={styles.productDescription} numberOfLines={1}>{product.description}</Text>
                              )}
                            </View>
                          </View>
                          {isSelected && (
                            <View style={styles.priceInputRow}>
                              <Text style={styles.priceLabel}>{t('your_price') || 'Hintapyyntösi'}:</Text>
                              <View style={styles.priceInputContainer}>
                                <TextInput
                                  style={styles.priceInput}
                                  value={productPrices[product.id] || ''}
                                  onChangeText={(text) => updatePrice(product.id, text)}
                                  placeholder="0.00"
                                  placeholderTextColor={theme.colors.text.secondary}
                                  keyboardType="decimal-pad"
                                />
                                <Text style={styles.currencyLabel}>€</Text>
                              </View>
                            </View>
                          )}
                        </TouchableOpacity>
                      )
                    })}
                  </View>
                )
              })}
            </View>
          )}
        </View>

        {/* Notes */}
        {draftProducts.length > 0 && selectedProducts.size > 0 && (
          <>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>{t('additional_notes') || 'Lisätiedot'}</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={notes}
                onChangeText={setNotes}
                placeholder={t('notes_placeholder') || 'Esim. toivottu noutoaika, yhteystiedot...'}
                placeholderTextColor={theme.colors.text.secondary}
                multiline
                numberOfLines={4}
              />
            </View>

            {/* Summary */}
            <View style={styles.summarySection}>
              <View style={styles.summaryRow}>
                <Text style={styles.summaryLabel}>{t('selected_products') || 'Valitut tuotteet'}</Text>
                <Text style={styles.summaryValue}>{selectedProducts.size} {t('pcs') || 'kpl'}</Text>
              </View>
              <View style={styles.summaryRow}>
                <Text style={styles.totalLabel}>{t('total_price') || 'Yhteensä'}</Text>
                <Text style={styles.totalPrice}>{calculateTotal().toFixed(2)} €</Text>
              </View>
            </View>

            {/* Submit Button */}
            <TouchableOpacity
              style={[styles.submitButton, submitting && styles.submitButtonDisabled]}
              onPress={submitOffer}
              disabled={submitting}
            >
              {submitting ? (
                <Spinner color="#fff" />
              ) : (
                <>
                  <Check size={20} color="#fff" />
                  <Text style={styles.submitButtonText}>{t('submit_offer') || 'Lähetä tarjous'}</Text>
                </>
              )}
            </TouchableOpacity>
          </>
        )}

        <View style={styles.bottomSpacer} />
      </ScrollView>
</View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background.primary },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center' },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingTop: Platform.OS === 'web' ? theme.spacing.md : 50,
    paddingBottom: theme.spacing.md,
    backgroundColor: theme.colors.background.card,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.ui.border },
  backButton: {
    padding: 8,
    marginLeft: -8 },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center' },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: theme.colors.text.primary },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  headerSpacer: {
    width: 40 },
  scrollContent: {
    flex: 1 },
  storeInfo: {
    backgroundColor: theme.colors.background.card,
    padding: theme.spacing.md,
    marginBottom: theme.spacing.sm },
  storeInfoLabel: {
    fontSize: 12,
    color: theme.colors.text.secondary,
    marginBottom: 4 },
  storeName: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  storeLocationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4 },
  storeAddress: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  dropoffInfo: {
    marginTop: theme.spacing.sm,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 8 },
  dropoffLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.text.secondary,
    marginBottom: 4 },
  dropoffText: {
    fontSize: 14,
    color: theme.colors.text.primary },
  section: {
    padding: theme.spacing.md },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginBottom: 4 },
  sectionSubtitle: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    marginBottom: theme.spacing.md },
  emptyState: {
    alignItems: 'center',
    paddingVertical: theme.spacing.xl },
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text.primary,
    marginTop: theme.spacing.md,
    marginBottom: theme.spacing.sm },
  emptyStateText: {
    fontSize: 14,
    color: theme.colors.text.secondary,
    textAlign: 'center',
    paddingHorizontal: theme.spacing.lg,
    marginBottom: theme.spacing.md },
  addProductsButton: {
    backgroundColor: theme.colors.brand.primary,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.sm,
    borderRadius: 8 },
  addProductsButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600' },
  productsList: {
    gap: theme.spacing.md },
  collectionGroup: {
    marginBottom: theme.spacing.sm },
  collectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xs,
    backgroundColor: theme.colors.background.secondary,
    borderRadius: 8,
    marginBottom: theme.spacing.xs },
  collectionHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1 },
  collectionHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8 },
  colorDot: {
    width: 12,
    height: 12,
    borderRadius: 6 },
  collectionName: {
    fontSize: 15,
    fontWeight: '600',
    color: theme.colors.text.primary },
  collectionCount: {
    fontSize: 13,
    color: theme.colors.text.secondary },
  selectAllButton: {
    paddingHorizontal: 8,
    paddingVertical: 4 },
  selectAllText: {
    fontSize: 13,
    color: theme.colors.brand.primary,
    fontWeight: '500' },
  productCard: {
    backgroundColor: theme.colors.background.card,
    borderRadius: 12,
    borderWidth: 2,
    borderColor: theme.colors.ui.border,
    overflow: 'hidden' },
  productCardSelected: {
    borderColor: theme.colors.brand.primary,
    backgroundColor: `${theme.colors.brand.primary}08` },
  productCardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: theme.spacing.sm,
    gap: theme.spacing.sm },
  selectionIndicator: {
    width: 32,
    alignItems: 'center' },
  productImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    backgroundColor: theme.colors.background.secondary },
  productImagePlaceholder: {
    justifyContent: 'center',
    alignItems: 'center' },
  productInfo: {
    flex: 1 },
  productTitle: {
    fontSize: 15,
    fontWeight: '500',
    color: theme.colors.text.primary },
  productDescription: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginTop: 2 },
  priceInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: theme.colors.ui.border,
    backgroundColor: theme.colors.background.secondary },
  priceLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  priceInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.background.card,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
    paddingHorizontal: theme.spacing.sm },
  priceInput: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary,
    paddingVertical: 8,
    minWidth: 80,
    textAlign: 'right' },
  currencyLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.secondary,
    marginLeft: 4 },
  input: {
    backgroundColor: theme.colors.background.card,
    borderWidth: 1,
    borderColor: theme.colors.ui.border,
    borderRadius: 8,
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.sm,
    fontSize: 15,
    color: theme.colors.text.primary },
  textArea: {
    minHeight: 100,
    textAlignVertical: 'top' },
  summarySection: {
    backgroundColor: theme.colors.background.card,
    marginHorizontal: theme.spacing.md,
    padding: theme.spacing.md,
    borderRadius: 12,
    marginBottom: theme.spacing.md },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing.xs },
  summaryLabel: {
    fontSize: 14,
    color: theme.colors.text.secondary },
  summaryValue: {
    fontSize: 14,
    color: theme.colors.text.primary },
  totalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.text.primary },
  totalPrice: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.colors.brand.primary },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: theme.colors.brand.primary,
    marginHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.md,
    borderRadius: 12 },
  submitButtonDisabled: {
    opacity: 0.6 },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff' },
  bottomSpacer: {
    height: 100 } })
